/* ==========================================================================
   we-pdp-bundles.js - "Frequently Bought Together" carousel.

   Loaded by sections/we-pdp-bundles.liquid. Vanilla, no dependencies.

   Self-contained under its own data-wep-bundle-* hooks rather than a shared
   carousel primitive in assets/we-pdp.js, so this file can change without
   touching or re-validating any other section. we-pdp.js is not involved.

   Scrolling itself is entirely native - a scroll-snap track handles touch and
   trackpad. There is deliberately NO touch or pointer gesture handling here:
   the horizontal-swipe behaviour this PDP relies on is the browser's own.

   Every listener is bound to an element inside the carousel, never to
   document or window for keyboard input, so this can never become a
   page-level key handler competing with anything else on the page.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function cardsOf(root) {
    return qsa('[data-wep-bundle-card]', root);
  }

  /* Which dot reads as current. Driven by the leftmost substantially-visible
     card, not by a scroll offset, so it stays right however the track moved. */
  function syncDots(root, index) {
    root.dataset.wepBundleIndex = String(index);

    qsa('[data-wep-bundle-dot]', root).forEach(function (dot, i) {
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }

  /* Prev/next disabled state is derived from the track's actual scroll
     position rather than from the active card index. With up to four cards
     visible at once the last card can never become the leftmost one, so an
     index-based check would leave "next" enabled but inert at the end of the
     track. */
  function syncNav(root, track) {
    var prev = qs('[data-wep-bundle-prev]', root);
    var next = qs('[data-wep-bundle-next]', root);
    var maxScroll = track.scrollWidth - track.clientWidth;

    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = maxScroll <= 1 || track.scrollLeft >= maxScroll - 1;
  }

  function goToCard(root, track, index) {
    var cards = cardsOf(root);
    if (!cards.length) return;

    var clamped = Math.max(0, Math.min(index, cards.length - 1));

    cards[clamped].scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      inline: 'start',
      /* 'nearest' so stepping the carousel never drags the page vertically. */
      block: 'nearest'
    });

    syncDots(root, clamped);
  }

  function step(root, track, delta) {
    var current = parseInt(root.dataset.wepBundleIndex || '0', 10);
    goToCard(root, track, current + delta);
  }

  function initCarousel(root) {
    if (root.dataset.wepBundleInit === 'true') return;
    root.dataset.wepBundleInit = 'true';

    var track = qs('[data-wep-bundle-track]', root);
    var cards = cardsOf(root);
    if (!track || !cards.length) return;

    var prev = qs('[data-wep-bundle-prev]', root);
    var next = qs('[data-wep-bundle-next]', root);

    if (prev) {
      prev.addEventListener('click', function () {
        step(root, track, -1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        step(root, track, 1);
      });
    }

    qsa('[data-wep-bundle-dot]', root).forEach(function (dot) {
      dot.addEventListener('click', function () {
        goToCard(root, track, parseInt(dot.dataset.wepBundleDotIndex || '0', 10));
      });
    });

    /* Bound to the track itself, not to document. Arrow keys only ever step
       this carousel while the track holds focus, so nothing else on the PDP
       can be affected. preventDefault stops the browser also scrolling the
       track natively, which would double-step it. */
    track.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      step(root, track, event.key === 'ArrowLeft' ? -1 : 1);
    });

    track.addEventListener('scroll', function () {
      syncNav(root, track);
    }, { passive: true });

    /* An orientation change or resize alters how many cards fit, and so
       whether either end of the track has been reached. */
    window.addEventListener('resize', function () {
      syncNav(root, track);
    });

    if ('IntersectionObserver' in window) {
      /* An observer callback only carries the entries that CHANGED, so the
         current active card cannot be derived from one batch alone - scrolling
         right can report "card 1 left" and "card 5 entered" together while the
         leftmost visible card is actually card 2. Visibility is therefore kept
         for every card and the lowest visible index wins. */
      var visible = [];
      var i;
      for (i = 0; i < cards.length; i++) visible.push(false);

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var index = cards.indexOf(entry.target);
            if (index !== -1) visible[index] = entry.intersectionRatio >= 0.6;
          });

          for (var j = 0; j < visible.length; j++) {
            if (visible[j]) {
              syncDots(root, j);
              return;
            }
          }
        },
        { root: track, threshold: [0.6] }
      );

      cards.forEach(function (card) {
        observer.observe(card);
      });
    }

    syncDots(root, 0);
    syncNav(root, track);
  }

  function init() {
    qsa('[data-wep-bundle-carousel]').forEach(initCarousel);
  }

  /* The section loads this with defer, so the DOM is normally already parsed;
     the readyState guard just makes the file safe to load any other way. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
